/**
 * The unlisted teams this session has already been shown.
 *
 * `visibleStartTeams` keeps the SELECTED private row visible, which is enough to
 * arrive on one but not to live with: a reader who follows a private link, opens
 * another team to compare, then wants their own one back finds it gone, with the
 * original link the only way to see it again. Anything they can reach by
 * selecting it is something they should be able to reach twice.
 *
 * So a reveal is recorded and stays recorded. It is not a new permission — the
 * row was already theirs to select a moment ago; this only stops the list
 * forgetting it mid-decision.
 *
 * KEYED ON THE SESSION, which is what makes Start Over and New Registration
 * clear it without anything having to remember to: both end this session and
 * open a new one, and the new id reads its own empty stash. SESSION storage
 * rather than local, so it belongs to this tab's visit and cannot surface in
 * another tab registering somewhere else.
 */

const stashKey = (sessionId: string) =>
  `registration_revealed_private_teams_${sessionId}`

/**
 * One team in one division.
 *
 * A team can hold registrations in several divisions, and revealing the row a
 * reader chose must say nothing about the others — the same narrowness
 * `visibleStartTeams` applies to the selection itself.
 */
export const privateTeamKey = (
  teamId: string | null | undefined,
  divisionId: string | null | undefined
): string | null => (teamId && divisionId ? `${teamId}::${divisionId}` : null)

/** The keys revealed in this session, or an empty list. */
export const readRevealedPrivateTeams = (
  sessionId: string | null | undefined
): string[] => {
  if (!sessionId || typeof window === 'undefined') {
    return []
  }

  try {
    const stored = window.sessionStorage?.getItem(stashKey(sessionId))
    if (!stored) {
      return []
    }
    const parsed: unknown = JSON.parse(stored)
    // Anything but a list of strings is treated as absent: this is a
    // convenience, and it must never throw into a render.
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((entry): entry is string => typeof entry === 'string')
  } catch {
    return []
  }
}

/**
 * Record that a private row has been shown, and report the new list.
 *
 * The caller holds the same list in state — the filter reads it during render,
 * and storage alone would not re-render.
 */
export const rememberRevealedPrivateTeam = (
  sessionId: string | null | undefined,
  teamId: string | null | undefined,
  divisionId: string | null | undefined
): string[] => {
  const key = privateTeamKey(teamId, divisionId)
  if (!sessionId || !key) {
    return readRevealedPrivateTeams(sessionId)
  }

  const current = readRevealedPrivateTeams(sessionId)
  if (current.includes(key)) {
    return current
  }
  const next = [...current, key]

  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage?.setItem(stashKey(sessionId), JSON.stringify(next))
    } catch {
      /*
       * Private-mode browsers throw. The list is still returned, so the row
       * survives this visit in memory; only a refresh forgets it, which leaves
       * the behaviour exactly as it was before this stash existed.
       */
    }
  }

  return next
}
